import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, MessageCircle, Send, Truck, Handshake, CheckCircle2, Clock, Globe, Shield, Loader2, ExternalLink, Navigation } from 'lucide-react';
import { companyInfo, certifications } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('pickup');
  const [formData, setFormData] = useState({ name: '', company: '', email: '', phone: '', message: '', assetType: '', quantity: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (location.hash === '#partner') setActiveTab('partner'); }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const endpoint = activeTab === 'pickup' ? '/api/contact/pickup' : '/api/contact/partner';
      const body = activeTab === 'pickup'
        ? { name: formData.name, company: formData.company, email: formData.email, phone: formData.phone, asset_type: formData.assetType, quantity: formData.quantity, message: formData.message }
        : { name: formData.name, company: formData.company, email: formData.email, phone: formData.phone, message: formData.message };
      const res = await fetch(`${API_URL}${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Failed to submit');
      setIsSubmitted(true);
      setFormData({ name: '', company: '', email: '', phone: '', message: '', assetType: '', quantity: '' });
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative pt-28 lg:pt-0 min-h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0a1628]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, rgba(79, 209, 197, 0.5) 1px, transparent 0)`, backgroundSize: '40px 40px' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-4">Contact Us</p>
              <h1 className="text-4xl lg:text-5xl font-light text-white mb-6">Let's <span className="font-semibold">Connect</span></h1>
              <p className="text-lg text-gray-400">Ready to start your sustainable journey? Get in touch for pickup requests or partnership inquiries.</p>
            </div>
            <div className="hidden lg:flex justify-end gap-4">
              {[ { icon: Clock, text: '24/7 Support' }, { icon: Globe, text: 'UAE-Wide Coverage' }, { icon: Shield, text: 'Certified Operations' } ].map((item, i) => (
                <div key={i} className="bg-white/5 border border-white/10 px-5 py-4">
                  <item.icon className="w-6 h-6 text-[#4fd1c5] mb-2" />
                  <span className="text-white text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form & Info */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="flex mb-10 border-b border-gray-200">
                <button onClick={() => { setActiveTab('pickup'); setIsSubmitted(false); setError(''); }}
                  data-testid="contact-tab-pickup"
                  className={`flex items-center gap-2 px-6 py-4 font-semibold border-b-2 -mb-px transition-colors ${activeTab === 'pickup' ? 'border-[#4fd1c5] text-[#0a1628]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                  <Truck className="w-5 h-5" /> Request Pickup
                </button>
                <button onClick={() => { setActiveTab('partner'); setIsSubmitted(false); setError(''); }}
                  data-testid="contact-tab-partner"
                  className={`flex items-center gap-2 px-6 py-4 font-semibold border-b-2 -mb-px transition-colors ${activeTab === 'partner' ? 'border-[#4fd1c5] text-[#0a1628]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                  <Handshake className="w-5 h-5" /> Partner With Us
                </button>
              </div>

              {isSubmitted ? (
                <div className="p-16 bg-[#f8fafc] text-center" data-testid="contact-success-message">
                  <div className="w-20 h-20 bg-[#4fd1c5] mx-auto mb-6 flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10 text-[#0a1628]" />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#0a1628] mb-3">Thank You!</h3>
                  <p className="text-gray-500">We've received your request and will get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  {error && <div className="p-4 bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="contact-error">{error}</div>}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[#0a1628] text-sm font-semibold mb-2">Full Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange} required data-testid="contact-input-name"
                        className="w-full px-5 py-4 bg-[#f8fafc] border-0 focus:outline-none focus:ring-2 focus:ring-[#4fd1c5]" placeholder="John Smith" />
                    </div>
                    <div>
                      <label className="block text-[#0a1628] text-sm font-semibold mb-2">Company *</label>
                      <input type="text" name="company" value={formData.company} onChange={handleInputChange} required data-testid="contact-input-company"
                        className="w-full px-5 py-4 bg-[#f8fafc] border-0 focus:outline-none focus:ring-2 focus:ring-[#4fd1c5]" placeholder="Your Company" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[#0a1628] text-sm font-semibold mb-2">Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} required data-testid="contact-input-email"
                        className="w-full px-5 py-4 bg-[#f8fafc] border-0 focus:outline-none focus:ring-2 focus:ring-[#4fd1c5]" placeholder="john@company.com" />
                    </div>
                    <div>
                      <label className="block text-[#0a1628] text-sm font-semibold mb-2">Phone</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} data-testid="contact-input-phone"
                        className="w-full px-5 py-4 bg-[#f8fafc] border-0 focus:outline-none focus:ring-2 focus:ring-[#4fd1c5]" placeholder="+971 50 000 0000" />
                    </div>
                  </div>
                  {activeTab === 'pickup' && (
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[#0a1628] text-sm font-semibold mb-2">Asset Type</label>
                        <select name="assetType" value={formData.assetType} onChange={handleInputChange} data-testid="contact-select-asset"
                          className="w-full px-5 py-4 bg-[#f8fafc] border-0 focus:outline-none focus:ring-2 focus:ring-[#4fd1c5]">
                          <option value="">Select type</option>
                          <option value="computers">Computers & Laptops</option>
                          <option value="servers">Servers & Data Center</option>
                          <option value="telecom">Telecom Equipment</option>
                          <option value="appliances">Electronics & Appliances</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[#0a1628] text-sm font-semibold mb-2">Estimated Quantity</label>
                        <input type="text" name="quantity" value={formData.quantity} onChange={handleInputChange} data-testid="contact-input-quantity"
                          className="w-full px-5 py-4 bg-[#f8fafc] border-0 focus:outline-none focus:ring-2 focus:ring-[#4fd1c5]" placeholder="e.g., 100 units" />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-[#0a1628] text-sm font-semibold mb-2">Message</label>
                    <textarea name="message" value={formData.message} onChange={handleInputChange} rows={5} data-testid="contact-input-message"
                      className="w-full px-5 py-4 bg-[#f8fafc] border-0 focus:outline-none focus:ring-2 focus:ring-[#4fd1c5] resize-none"
                      placeholder={activeTab === 'pickup' ? 'Tell us about your assets and recycling requirements...' : 'Tell us about your partnership interests and how we can collaborate...'} />
                  </div>
                  <button type="submit" disabled={isLoading} data-testid="contact-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#0a1628] hover:bg-[#4fd1c5] text-white hover:text-[#0a1628] px-8 py-5 font-semibold text-lg transition-colors disabled:opacity-50">
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    {isLoading ? 'Submitting...' : (activeTab === 'pickup' ? 'Submit Pickup Request' : 'Submit Partnership Inquiry')}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-28">
                <h3 className="text-xl font-semibold text-[#0a1628] mb-8">Get in Touch</h3>
                <div className="space-y-4 mb-10">
                  {/* Office */}
                  <a href={companyInfo.officeMapUrl} target="_blank" rel="noopener noreferrer"
                    className="block p-6 bg-[#0a1628] group hover:bg-[#0d1d35] transition-colors" data-testid="contact-office-link">
                    <div className="flex items-start gap-4">
                      <MapPin className="w-6 h-6 text-[#4fd1c5] flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-white font-semibold mb-1 flex items-center gap-2">
                          Head Office
                          <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-[#4fd1c5]" />
                        </h4>
                        <p className="text-gray-400 text-sm">{companyInfo.headOffice}</p>
                        <div className="flex gap-3 mt-3">
                          <span className="text-[#4fd1c5] text-xs font-semibold flex items-center gap-1">
                            <Navigation className="w-3 h-3" /> Google Maps
                          </span>
                          <a href={companyInfo.officeWazeUrl} target="_blank" rel="noopener noreferrer"
                            className="text-[#4fd1c5] text-xs font-semibold flex items-center gap-1 hover:underline"
                            onClick={e => e.stopPropagation()}>
                            <Navigation className="w-3 h-3" /> Waze
                          </a>
                        </div>
                      </div>
                    </div>
                  </a>

                  {/* Facility - Dubai */}
                  <a href={companyInfo.facilityMapUrl} target="_blank" rel="noopener noreferrer"
                    className="block p-6 bg-[#f8fafc] group hover:bg-gray-100 transition-colors" data-testid="contact-facility-link">
                    <div className="flex items-start gap-4">
                      <MapPin className="w-6 h-6 text-[#4fd1c5] flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-[#0a1628] font-semibold mb-1 flex items-center gap-2">
                          Facility - Dubai
                          <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-[#4fd1c5]" />
                        </h4>
                        <p className="text-gray-500 text-sm">{companyInfo.location}</p>
                        <div className="flex gap-3 mt-3">
                          <span className="text-[#4fd1c5] text-xs font-semibold flex items-center gap-1">
                            <Navigation className="w-3 h-3" /> Google Maps
                          </span>
                          <a href={companyInfo.facilityWazeUrl} target="_blank" rel="noopener noreferrer"
                            className="text-[#4fd1c5] text-xs font-semibold flex items-center gap-1 hover:underline"
                            onClick={e => e.stopPropagation()}>
                            <Navigation className="w-3 h-3" /> Waze
                          </a>
                        </div>
                      </div>
                    </div>
                  </a>

                  {/* Facility - Sharjah */}
                  <a href={companyInfo.facility2MapUrl} target="_blank" rel="noopener noreferrer"
                    className="block p-6 bg-[#f8fafc] group hover:bg-gray-100 transition-colors" data-testid="contact-facility2-link">
                    <div className="flex items-start gap-4">
                      <MapPin className="w-6 h-6 text-[#4fd1c5] flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-[#0a1628] font-semibold mb-1 flex items-center gap-2">
                          Facility - Sharjah
                          <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-[#4fd1c5]" />
                        </h4>
                        <p className="text-gray-500 text-sm">{companyInfo.facility2}</p>
                        <div className="flex gap-3 mt-3">
                          <span className="text-[#4fd1c5] text-xs font-semibold flex items-center gap-1">
                            <Navigation className="w-3 h-3" /> Google Maps
                          </span>
                          <a href={companyInfo.facility2WazeUrl} target="_blank" rel="noopener noreferrer"
                            className="text-[#4fd1c5] text-xs font-semibold flex items-center gap-1 hover:underline"
                            onClick={e => e.stopPropagation()}>
                            <Navigation className="w-3 h-3" /> Waze
                          </a>
                        </div>
                      </div>
                    </div>
                  </a>

                  <div className="grid grid-cols-2 gap-4">
                    <a href={`tel:${companyInfo.hotline}`} className="p-6 bg-[#f8fafc] hover:bg-[#4fd1c5]/10 transition-colors group">
                      <Phone className="w-6 h-6 text-[#4fd1c5] mb-3" />
                      <h4 className="text-[#0a1628] font-semibold text-sm mb-1">Phone</h4>
                      <p className="text-gray-500 text-sm group-hover:text-[#4fd1c5] transition-colors">{companyInfo.hotline}</p>
                    </a>
                    <a href={`mailto:${companyInfo.email}`} className="p-6 bg-[#f8fafc] hover:bg-[#4fd1c5]/10 transition-colors group">
                      <Mail className="w-6 h-6 text-[#4fd1c5] mb-3" />
                      <h4 className="text-[#0a1628] font-semibold text-sm mb-1">Email</h4>
                      <p className="text-gray-500 text-sm group-hover:text-[#4fd1c5] transition-colors truncate">{companyInfo.email}</p>
                    </a>
                  </div>

                  <a href={`https://wa.me/${companyInfo.whatsapp?.replace(/\s/g, '')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 p-5 bg-[#25D366] hover:bg-[#20bd5a] text-white transition-colors">
                    <MessageCircle className="w-6 h-6" />
                    <span className="font-semibold">Chat on WhatsApp</span>
                  </a>
                </div>

                {/* Certifications - ISOs only */}
                <div className="p-6 bg-[#f8fafc]">
                  <h4 className="text-sm font-semibold text-[#0a1628] uppercase tracking-wider mb-4">Certifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {certifications.map((cert) => (
                      <span key={cert} className="px-3 py-2 bg-white border border-gray-200 text-xs font-semibold text-[#0a1628]">{cert}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - Three marked locations */}
      <section className="bg-[#f8fafc] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h3 className="text-sm font-semibold text-[#0a1628] uppercase tracking-wider mb-6">Our Locations</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Head Office */}
            <div className="bg-white border border-gray-200 overflow-hidden hover:border-[#4fd1c5] transition-colors">
              <div className="h-[220px]">
                <iframe
                  title="Head Office - Dubai"
                  src="https://maps.google.com/maps?q=The+Business+Center+Burdubai+Dubai+UAE&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                  className="w-full h-full"
                />
              </div>
              <a href={companyInfo.officeMapUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 group" data-testid="map-office-link">
                <MapPin className="w-5 h-5 text-[#4fd1c5] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-[#0a1628]">Head Office</h4>
                  <p className="text-xs text-gray-500 truncate">{companyInfo.headOffice}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#4fd1c5] transition-colors flex-shrink-0" />
              </a>
            </div>

            {/* Facility Dubai */}
            <div className="bg-white border border-gray-200 overflow-hidden hover:border-[#4fd1c5] transition-colors">
              <div className="h-[220px]">
                <iframe
                  title="Facility - Dubai"
                  src="https://maps.google.com/maps?q=Jebel+Ali+Free+Zone+Authority+Dubai+UAE&t=&z=14&ie=UTF8&iwloc=&output=embed"
                  width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                  className="w-full h-full"
                />
              </div>
              <a href={companyInfo.facilityMapUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 group" data-testid="map-facility-link">
                <MapPin className="w-5 h-5 text-[#4fd1c5] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-[#0a1628]">Facility - Dubai</h4>
                  <p className="text-xs text-gray-500 truncate">{companyInfo.location}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#4fd1c5] transition-colors flex-shrink-0" />
              </a>
            </div>

            {/* Facility Sharjah */}
            <div className="bg-white border border-gray-200 overflow-hidden hover:border-[#4fd1c5] transition-colors">
              <div className="h-[220px]">
                <iframe
                  title="Facility - Sharjah"
                  src="https://maps.google.com/maps?q=Hamriyah+Free+Zone+Phase+2+Sharjah+UAE&t=&z=14&ie=UTF8&iwloc=&output=embed"
                  width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                  className="w-full h-full"
                />
              </div>
              <a href={companyInfo.facility2MapUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 group" data-testid="map-facility2-link">
                <MapPin className="w-5 h-5 text-[#4fd1c5] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-[#0a1628]">Facility - Sharjah</h4>
                  <p className="text-xs text-gray-500 truncate">{companyInfo.facility2}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#4fd1c5] transition-colors flex-shrink-0" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
